import { httpClient } from "../http-client";
import { NEW_API_ENDPOINTS } from "shared/constants/api";
import type {
  ApiResponse,
  Pageable,
  ApiResponseCompanyAdminCompanyInfoResponse,
  UpdateCompanyInfoRequest,
} from "shared/types/api";
import type {
  CompanyEquipmentStatus,
  CompanyOverview,
  CompanyWithStatus,
  CompanyWithStatusPage,
  DataReportLogPage,
  DataReportSummary,
} from "domain/types/admin-company";

/**
 * 어드민 "등록 업체 관리" 화면 (ManageRegisteredCompanies1) 전용 API 서비스.
 *
 * 회사별 KPI 보고/수집기 상태까지 한 번에 묶어 내려주는
 * `/api/admin/company/with-status` + `/action-required` 를 호출한다.
 */
export class AdminCompanyApiService {
  /** 등록된 회사 + 상태 목록 (페이징). */
  async listCompaniesWithStatus(
    pageable: Pageable,
  ): Promise<ApiResponse<CompanyWithStatusPage>> {
    const queryParams = new URLSearchParams();
    Object.entries(pageable).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((v) => queryParams.append(key, String(v)));
      } else {
        queryParams.append(key, String(value));
      }
    });

    const url = `${NEW_API_ENDPOINTS.adminController.GET.listCompaniesWithStatus}?${queryParams.toString()}`;
    const response = await httpClient.get<ApiResponse<CompanyWithStatusPage>>(url);

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "등록 업체 목록을 가져오지 못했습니다.",
      );
    }
    return response.data;
  }

  /** 조치 필요 업체 목록 — 임박도 순 정렬. */
  async listActionRequiredCompanies(): Promise<ApiResponse<CompanyWithStatus[]>> {
    const response = await httpClient.get<ApiResponse<CompanyWithStatus[]>>(
      NEW_API_ENDPOINTS.adminController.GET.actionRequiredCompanies,
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "조치 필요 업체 목록을 가져오지 못했습니다.",
      );
    }
    return response.data;
  }

  /**
   * 회사 상세 — Overview 탭 본문 통합 조회.
   *
   * 자재 보유 충분율 / KPI 수집률 / 대표 장비 메타 / 최근 보고 5건 /
   * 실시간 장비 현황 / 일일 처리 현황을 한 번에 묶어 받는다.
   *
   * 알람/알림 카드 2종은 백엔드 집계 미구현 → 응답에 포함되지 않음.
   * 프론트는 해당 카드만 별도 mock 으로 채운다.
   */
  async getCompanyOverview(
    companyId: number,
  ): Promise<ApiResponse<CompanyOverview>> {
    const response = await httpClient.get<ApiResponse<CompanyOverview>>(
      NEW_API_ENDPOINTS.adminController.GET.companyOverview(companyId),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "회사 Overview 정보를 가져오지 못했습니다.",
      );
    }
    return response.data;
  }

  /**
   * 회사 상세 — DataReport 탭 요약 카드 4종.
   *
   * 보고 상태 / 마지막 보고 / 누적 보고 (최근 30일) / 전일 미보고 program 수.
   */
  async getDataReportSummary(
    companyId: number,
  ): Promise<ApiResponse<DataReportSummary>> {
    const response = await httpClient.get<ApiResponse<DataReportSummary>>(
      NEW_API_ENDPOINTS.adminController.GET.companyDataReportSummary(companyId),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg ||
          "DataReport 요약 정보를 가져오지 못했습니다.",
      );
    }
    return response.data;
  }

  /**
   * 회사 상세 — DataReport 탭 로그 테이블 (페이징).
   *
   * LEVEL1 + LEVEL2/3 로그를 transmittedAt 역순으로 합쳐 페이징한다.
   * LEVEL2/3 한 건은 측정값 행 + 달성률 행 두 행으로 분리되어 응답된다.
   */
  async getDataReportLogs(
    companyId: number,
    pageable: Pageable,
  ): Promise<ApiResponse<DataReportLogPage>> {
    const queryParams = new URLSearchParams();
    Object.entries(pageable).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((v) => queryParams.append(key, String(v)));
      } else {
        queryParams.append(key, String(value));
      }
    });
    const query = queryParams.toString();
    const url = `${NEW_API_ENDPOINTS.adminController.GET.companyDataReportLogs(companyId)}${
      query ? `?${query}` : ""
    }`;
    const response = await httpClient.get<ApiResponse<DataReportLogPage>>(url);

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "DataReport 로그를 가져오지 못했습니다.",
      );
    }
    return response.data;
  }

  /**
   * 회사 상세 — EquipmentStatus 탭 본문 통합 조회.
   *
   * 대표 장비 메타(Overview 와 동일 형태 재사용) + 메타 스냅샷 3종 +
   * 연결 상태 4종을 한 번에 묶어 받는다.
   *
   * KPI 보고 설정 섹션(인증키/토글) 은 별도 엔드포인트가 담당한다 — 본 응답에 포함되지 않음.
   */
  async getCompanyEquipmentStatus(
    companyId: number,
    machineId?: number,
  ): Promise<ApiResponse<CompanyEquipmentStatus>> {
    const response = await httpClient.get<ApiResponse<CompanyEquipmentStatus>>(
      NEW_API_ENDPOINTS.adminController.GET.companyEquipmentStatus(companyId),
      machineId != null ? { params: { machineId } } : undefined,
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg ||
          "EquipmentStatus 정보를 가져오지 못했습니다.",
      );
    }
    return response.data;
  }

  /**
   * 회사 정보 수정 (어드민 전용).
   *
   * 회사 관리자(MES) 의 회사정보 수정과 동일한 필드/파일을 어드민이 임의 회사에 대해 수정한다.
   * 회사용 `PUT /api/company/{id}` 와 분리된 어드민 전용 엔드포인트 `PUT /api/admin/company/{id}` 를 호출한다.
   */
  async updateCompanyInfo(
    request: UpdateCompanyInfoRequest,
  ): Promise<ApiResponseCompanyAdminCompanyInfoResponse> {
    const formData = new FormData();

    formData.append("companyId", request.companyId.toString());
    formData.append("companyAdminId", request.companyAdminId.toString());
    formData.append("companyName", request.companyName);

    if (request.ceoName) formData.append("ceoName", request.ceoName);
    if (request.domain) formData.append("domain", request.domain);
    if (request.phone) formData.append("phone", request.phone);
    if (request.secondaryContactType) {
      formData.append("secondaryContactType", request.secondaryContactType);
    }
    if (request.secondaryContact) {
      formData.append("secondaryContact", request.secondaryContact);
    }
    if (request.zipCode) formData.append("zipCode", request.zipCode);
    if (request.address) formData.append("address", request.address);
    if (request.addressDetail)
      formData.append("addressDetail", request.addressDetail);
    if (request.businessNumber)
      formData.append("businessNumber", request.businessNumber);
    if (request.businessType)
      formData.append("businessType", request.businessType);
    if (request.businessCategory)
      formData.append("businessCategory", request.businessCategory);
    if (request.homeTaxKey) formData.append("homeTaxKey", request.homeTaxKey);
    if (request.tossPayKey) formData.append("tossPayKey", request.tossPayKey);
    if (request.channelTalkKey)
      formData.append("channelTalkKey", request.channelTalkKey);

    if (request.logoFile) {
      formData.append("logoFile", request.logoFile);
    }
    if (request.faviconFile) {
      formData.append("faviconFile", request.faviconFile);
    }
    if (request.businessRegistrationFile) {
      formData.append(
        "businessRegistrationFile",
        request.businessRegistrationFile,
      );
    }
    if (request.companySealImageFile) {
      formData.append("companySealImageFile", request.companySealImageFile);
    }

    if (request.ecommerceBizNumber) {
      formData.append("ecommerceBizNumber", request.ecommerceBizNumber);
    }
    if (request.businessHours) {
      formData.append("businessHours", request.businessHours);
    }
    if (request.lunchBreak) {
      formData.append("lunchBreak", request.lunchBreak);
    }
    if (request.businessHoursNotice) {
      formData.append("businessHoursNotice", request.businessHoursNotice);
    }
    // 빈 문자열이면 전송하지 않아 백엔드에서 null 로 비워진다(푸터 줄 삭제).
    if (request.additionalBusinessInfo) {
      formData.append("additionalBusinessInfo", request.additionalBusinessInfo);
    }

    const response =
      await httpClient.put<ApiResponseCompanyAdminCompanyInfoResponse>(
        NEW_API_ENDPOINTS.adminController.PUT.updateCompanyInfo(
          request.companyId,
        ),
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "회사 정보 수정에 실패했습니다.",
      );
    }

    return response.data;
  }
}

export const adminCompanyApiService = new AdminCompanyApiService();